import axios, { AxiosRequestConfig } from "axios";
import dayjs from "dayjs";
import formatCurrency from "./formatCurrency";
import { useCustomAttributeMetadata } from "~/stores/customAttributeMetadata";
import { magento } from "~/config/extensionsApi";
import { computed, useContext } from "@nuxtjs/composition-api";

/**
 * Checks if the code is running on the server.
 */
export const isServer: boolean = typeof window === "undefined";

/**
 * Converts image URL to use webp format.
 * @param url - The original image URL.
 * @returns The converted image URL.
 */
export const convertImage = (url: string): string => {
  return url?.replace("jpg", "webp")?.replace("png", "webp")?.replace("jpeg", "webp");
};

/**
 * Checks if a link is a CMS link.
 * @param links - The link to check.
 * @returns True if the link is a CMS link, otherwise false.
 */
export const checkCmsLink = (links: string): boolean => {
  if (links && typeof links === "string" && links?.includes("cms/")) {
    return true;
  } else {
    return false;
  }
};

export const direct_link = (link: string) => {
  if (typeof window !== 'undefined') {
    window.location.href = process.env.API_BASE_URL + "/" + link;
  }
};

/**
 * Checks if a link is an HTTP link.
 * @param link - The link to check.
 * @returns True if the link is an HTTP link, otherwise false.
 */
export const checkHttpLink = (link: string): boolean => {
  let links = `${link}`;
  if (links) {
    if (links.indexOf("http") !== -1) {
      return true;
    } else {
      return false;
    }
  } else {
    return false;
  }
};

/**
 * Returns the original image URL.
 * @param url - The image URL.
 * @returns The original image URL.
 */
export const urlImage = (url: string): string => {
  return url;
};

/**
 * Converts the image URL to use webp format.
 * @param url - The original image URL.
 * @returns The converted image URL.
 */
export const changeImage = (url: string): string => {
  return convertImage(url);
};

/**
 * Set hiển thị cho title voucher
 */
export function setTitleVoucher(item) {
  let typeText = "";
  if (item && item?.value !== undefined && item?.value !== null) {
    if (item?.value === 0) {
      typeText = "Voucher";
    } else {
      if (item.suffix === "%" || item?.discount_type === "percent" || item?.discount_type === "percentage") {
        typeText = "Voucher " + item.value + "%";
      } else {
        typeText = item.value % 1000 === 0 ? "Voucher " + item.value / 1000 + "K" : "Voucher " + item.value + "VNĐ";
      }
    }
  } else {
    typeText = "Voucher";
  }
  return typeText;
}

/**
 * convert date time to format DD/MM/YYYY - HH:mm
 * @param time
 */
export function convertTime(time) {
  const date = dayjs(time).add(7, "hour");
  return date.format("DD/MM/YYYY - HH:mm");
}

/**
 * fromat price to VND
 * @param orderPrice
 */
export function formatPriceToVnd(orderPrice) {
  const currency = "VND";
  return formatCurrency(orderPrice, "vi-VN", { currency });
}

/**
 * decode uid => id
 */
export const decodeUid = (uid: string) => {
  return parseInt(Buffer.from(uid, "base64").toString("utf-8")); // Buffer.from() giải mã Base64, parseInt() chuyển thành số
};

/**
 * encode id => uid
 */
export const encodeId = (id: number) => {
  return Buffer.from(id.toString(), "utf-8").toString("base64");
};

export const copyCode = (code: string) => {
  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    navigator.clipboard.writeText(code);
  }
};

export const nl2br = (str: string, is_xhtml: string = undefined) => {
  if (typeof str === "undefined" || str === null) {
    return "";
  }
  var breakTag = is_xhtml || typeof is_xhtml === "undefined" ? "<br />" : "<br>";
  return (str + "").replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, "$1" + breakTag + "$2");
};

export const getDataAxios = async (url: string) => {
  try {
    let result = await axios.get(url);
    if (result?.data?.result) {
      return result?.data?.result;
    } else {
      return null;
    }
  } catch (error) {
    throw new Error(error?.message || "Some thing went wrong");
  }
};

export const postDataAxios = async (url: string, data) => {
  try {
    let result = await axios.post(url, data);
    if (result?.data?.result) {
      return result?.data?.result;
    } else {
      return null;
    }
  } catch (error) {
    throw new Error(error?.message || "Some thing went wrong");
  }
};

export const postApiAxios = async (url: string, data: any, config: AxiosRequestConfig = {}) => {
  try {
    const result = await axios.post(url, data, config);
    return result.data;
  } catch (error) {
    throw new Error(error?.message || "Something went wrong");
  }
};

export const getApiAxios = async (url: string, config: AxiosRequestConfig = {}, params: any = {}) => {
  try {
    const finalConfig = { ...config, params };
    const result = await axios.get(url, finalConfig);
    if (result.data?.code === 200 && result.data) {
      return result.data;
    }
    return null;
  } catch (error) {
    throw new Error(error?.message || "Something went wrong");
  }
};

export const getBgColorValue = (option_value: string) => {
  const baseUrl = magento.urlSwatch;
  let color = { "background-image": "#000" };
  const customAttributeMetadataStore = useCustomAttributeMetadata();
  const masterColorAttributes = computed(() => customAttributeMetadataStore.getMasterColor);

  const attribute_option = masterColorAttributes.value?.attribute_options.find((option) => option.value?.toString() === option_value?.toString());
  if (attribute_option && attribute_option?.swatch?.value) {
    const urlImage = baseUrl + attribute_option?.swatch?.value;
    color = {
      "background-image": `url("${urlImage}")`,
    };
  }
  return color;
};

export const getSizelabel = (sizeId) => {
  let sizeLabel = "";
  const customAttributeMetadataStore = useCustomAttributeMetadata();
  const sizeAttributes = computed(() => customAttributeMetadataStore.getSizes);

  const attribute_option = sizeAttributes.value?.attribute_options.find((option) => option?.value?.toString() === sizeId?.toString());
  if (attribute_option && attribute_option?.label) {
    sizeLabel = attribute_option.label;
  }
  return sizeLabel;
};

export const getColorLabel = (colorId) => {
  let colorLabel = "";
  const customAttributeMetadataStore = useCustomAttributeMetadata();
  const colorAttributes = computed(() => customAttributeMetadataStore.getMasterColor);

  const attribute_option = colorAttributes.value?.attribute_options.find((option) => option?.value?.toString() === colorId?.toString());
  if (attribute_option && attribute_option?.label) {
    colorLabel = attribute_option.label;
  }
  return colorLabel;
};

export const getLableSaleColor = (option_value: Number) => {
  const customAttributeMetadataStore = useCustomAttributeMetadata();
  const lableSaleProducts = computed(() => customAttributeMetadataStore.getLableSaleProduct);

  const attribute_option = lableSaleProducts.value?.attribute_options.find((option) => option.value?.toString() == option_value?.toString());

  if (attribute_option && attribute_option?.label) {
    const label = attribute_option?.label;
    return label;
  }

  return "";
};

export const getPromotionImageDetail = (option_value: String) => {
  const baseUrl = magento.urlSwatch;

  const customAttributeMetadataStore = useCustomAttributeMetadata();
  const imageDetailPromotion = computed(() => customAttributeMetadataStore.getImageDetailPromotion);
  const attribute_option = imageDetailPromotion.value?.attribute_options.find((option) => option.value?.toString() == option_value?.toString());

  if (attribute_option && attribute_option?.swatch?.value) {
    const urlImage = baseUrl + attribute_option?.swatch?.value;

    return urlImage;
  }

  return "";
};

export const getPromotionImageList = (option_value: String) => {
  const baseUrl = magento.urlSwatch;

  const customAttributeMetadataStore = useCustomAttributeMetadata();
  const imageListPromotion = computed(() => customAttributeMetadataStore.getImageListPromotion);
  const attribute_option = imageListPromotion.value?.attribute_options.find((option) => option.value?.toString() == option_value?.toString());

  if (attribute_option && attribute_option?.swatch?.value) {
    const urlImage = baseUrl + attribute_option?.swatch?.value;

    return urlImage;
  }

  return "";
};

export const getPromotionImageLink = (option_value: String) => {
  const customAttributeMetadataStore = useCustomAttributeMetadata();
  const imageListPromotion = computed(() => customAttributeMetadataStore.getImageDetailPromotion);
  const attribute_option = imageListPromotion.value?.attribute_options.find((option) => option.value?.toString() == option_value?.toString());

  if (attribute_option && attribute_option?.swatch_link) {
    const urlLink = "/" + attribute_option?.swatch_link;

    return urlLink;
  }

  return "";
};

export const getCampainProductDetail = (option_value: String) => {
  const customAttributeMetadataStore = useCustomAttributeMetadata();
  const campainProducts = computed(() => customAttributeMetadataStore.getCampainProductDetail);

  const attribute_option = campainProducts.value?.attribute_options.find((option) => option.value?.toString() == option_value?.toString());

  if (attribute_option && attribute_option?.label) {
    const label = attribute_option?.label;
    return label;
  }

  return "";
};

/**
 * Tính số ngày còn lại đến hết hạn
 * @param expiryDate - The expiry date in string format.
 * @returns The number of days until the expiry date.
 */
export const getDaysUntilExpiry = (expiryDate: string): number => {
  const today = new Date();
  const expiry = new Date(expiryDate);

  const oneDay = 24 * 60 * 60 * 1000;
  const diffDays = Math.round(Math.abs((expiry.getTime() - today.getTime()) / oneDay));
  return diffDays;
};

/**
 * Tính số giờ còn lại đến hết hạn
 * @param toDate - The end date in string format.
 * @returns The number of hours remaining until the end of the specified date.
 */
export const getCurrentTime = (toDate: string): number => {
  const now = new Date();
  const endOfDay = new Date(toDate);

  // Thiết lập thời gian là 23:59:59
  endOfDay.setHours(23, 59, 59, 999);

  const diffMilliseconds = endOfDay.getTime() - now.getTime();

  // Tính toán số giờ từ diffMilliseconds
  const diffHours = Math.floor(diffMilliseconds / (1000 * 60 * 60));

  return diffHours;
};

/**
 * Gọi lại hàm queryFn n lần nếu queryFn thất bại
 * @param queryFn - The function to call.
 */
export const fetchWithRetry = async <T>(fetchFn: () => Promise<T>, maxRetries = 3, retryDelay = 500): Promise<T> => {
  let lastError: any = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fetchFn();
    } catch (err) {
      lastError = err;
      console.error(`Fetch attempt ${attempt + 1}/${maxRetries} failed:`, err);

      // Kiểm tra nếu là lỗi HTTP/2, có thể không retry
      if (err.message && err.message.includes("ERR_HTTP2_PROTOCOL_ERROR")) {
        console.error("HTTP/2 Protocol Error detected, may not retry:", err);
        break; // Hoặc quyết định có retry hay không dựa trên trường hợp cụ thể
      }

      if (attempt < maxRetries - 1) {
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
        retryDelay *= 2; // Exponential backoff
      }
    }
  }

  throw lastError || new Error("All retry attempts failed");
};

export const isMobile = () => {
  const { app } = useContext();
  const isMobile = app && app.$device && app.$device.isMobile;
  return isMobile || false;
};

export const sortSizeOptions = (options) => {
  let defaultSizeText = ["XXXS", "XXS", "XS", "S", "M", "L", "XL", "XXL", "XXXL"];
  let sizeText = [];
  let sizeNumber = [];

  defaultSizeText.forEach((label, i) => {
    options.forEach((value, j) => {
      if (label === value) {
        sizeText.push(value);
      }
    });
  });
  options.forEach((value, i) => {
    let existInDefaultSizeText = false;
    defaultSizeText.forEach((label, j) => {
      if (value === label) {
        existInDefaultSizeText = true;
      }
    });
    if (!existInDefaultSizeText) {
      sizeNumber.push(value);
    }
  });
  sizeNumber.sort((a, b) => a - b);

  return [...sizeText, ...sizeNumber];
};
